import { useEffect, useRef } from 'react';
import { useAnalyticsTracking } from '../../hooks/useAnalyticsTracking';

export function ScrollTracker() {
  const { trackScrollDepth, trackTimeOnPage, trackSectionView } = useAnalyticsTracking();
  const scrollDepthTracked = useRef(new Set<number>());
  const timeOnPageTracked = useRef(new Set<number>());
  const startTime = useRef(Date.now());
  const sectionObserver = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Ensure functions are available
    if (!trackScrollDepth || !trackTimeOnPage || !trackSectionView) {
      console.warn('Analytics functions not available');
      return;
    }

    try {
      // Track scroll depth
      const handleScroll = () => {
        try {
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
          
          if (scrollHeight <= 0) return; // Prevent division by zero
          
          const scrollPercentage = Math.round((scrollTop / scrollHeight) * 100);

          // Track milestone percentages
          const milestones = [25, 50, 75, 90, 100];
          milestones.forEach(milestone => {
            if (scrollPercentage >= milestone && !scrollDepthTracked.current.has(milestone)) {
              scrollDepthTracked.current.add(milestone);
              trackScrollDepth(milestone, {
                page_type: 'landing',
                scroll_time: Date.now() - startTime.current,
                total_height: document.documentElement.scrollHeight,
                viewport_height: window.innerHeight
              });
            }
          });
        } catch (error) {
          console.warn('Error tracking scroll depth:', error);
        }
      };

      // Track time on page
      const trackTimeIntervals = () => {
        try {
          const timeOnPage = Date.now() - startTime.current;
          const timeInSeconds = Math.floor(timeOnPage / 1000);
          
          // Track time milestones (30s, 1m, 2m, 5m, 10m)
          const timeMilestones = [30, 60, 120, 300, 600];
          timeMilestones.forEach(milestone => {
            if (timeInSeconds >= milestone && !timeOnPageTracked.current.has(milestone)) {
              timeOnPageTracked.current.add(milestone);
              trackTimeOnPage(milestone, {
                page_type: 'landing',
                scroll_percentage: Math.round((window.pageYOffset / Math.max(document.documentElement.scrollHeight - window.innerHeight, 1)) * 100),
                user_active: true
              });
            }
          });
        } catch (error) {
          console.warn('Error tracking time on page:', error);
        }
      };

      // Track section visibility
      const setupSectionObserver = () => {
        try {
          const sections = document.querySelectorAll('section, .hero, .features-section, .how-it-works, .testimonials, .experience-section, footer');
          
          if (sections.length === 0) return;

          sectionObserver.current = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  try {
                    const sectionName = getSectionName(entry.target);
                    const visibilityPercentage = Math.round(entry.intersectionRatio * 100);
                    
                    trackSectionView(sectionName, {
                      section_element: entry.target.tagName.toLowerCase(),
                      visibility_percentage: visibilityPercentage,
                      time_on_page: Date.now() - startTime.current,
                      scroll_position: window.pageYOffset,
                      section_position: getSectionPosition(entry.target)
                    });
                  } catch (error) {
                    console.warn('Error tracking section view:', error);
                  }
                }
              });
            },
            { 
              threshold: [0.1, 0.25, 0.5, 0.75, 1.0],
              rootMargin: '50px'
            }
          );

          sections.forEach(section => {
            if (sectionObserver.current) {
              sectionObserver.current.observe(section);
            }
          });
        } catch (error) {
          console.warn('Error setting up section observer:', error);
        }
      };

      // Helper function to get section name
      const getSectionName = (element: Element): string => {
        try {
          // Check for ID first
          if (element.id) {
            return element.id.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
          }
          
          // Check for class names
          const classList = Array.from(element.classList);
          if (classList.includes('hero')) return 'Hero Section';
          if (classList.includes('features-section')) return 'Features Section';
          if (classList.includes('how-it-works')) return 'How It Works Section';
          if (classList.includes('testimonials')) return 'Testimonials Section';
          if (classList.includes('experience-section')) return 'Experience Section';
          if (element.tagName.toLowerCase() === 'footer') return 'Footer Section';
          
          // Check for heading content
          const heading = element.querySelector('h1, h2, h3');
          if (heading) {
            return heading.textContent?.trim() || 'Unknown Section';
          }
          
          return `${element.tagName.toLowerCase()} Section`;
        } catch (error) {
          return 'Unknown Section';
        }
      };

      // Helper function to get section position
      const getSectionPosition = (element: Element): string => {
        try {
          const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
          const totalHeight = document.documentElement.scrollHeight;
          
          if (totalHeight <= 0) return 'unknown';
          
          const positionPercentage = (elementTop / totalHeight) * 100;
          
          if (positionPercentage < 20) return 'top';
          if (positionPercentage < 40) return 'upper';
          if (positionPercentage < 60) return 'middle';
          if (positionPercentage < 80) return 'lower';
          return 'bottom';
        } catch (error) {
          return 'unknown';
        }
      };

      // Set up event listeners
      window.addEventListener('scroll', handleScroll, { passive: true });
      
      // Set up time tracking interval
      const timeInterval = setInterval(trackTimeIntervals, 1000);
      
      // Set up section observer with delay to ensure DOM is ready
      const setupTimeout = setTimeout(setupSectionObserver, 1000);
      
      // Track initial page load
      trackTimeOnPage(0, {
        page_type: 'landing',
        initial_load: true,
        referrer: document.referrer,
        user_agent: navigator.userAgent
      });

      return () => {
        window.removeEventListener('scroll', handleScroll);
        clearInterval(timeInterval);
        clearTimeout(setupTimeout);
        if (sectionObserver.current) {
          sectionObserver.current.disconnect();
        }
      };
    } catch (error) {
      console.error('Error setting up ScrollTracker:', error);
    }
  }, [trackScrollDepth, trackTimeOnPage, trackSectionView]);

  // Track page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      const timeOnPage = Date.now() - startTime.current;
      
      trackTimeOnPage(Math.floor(timeOnPage / 1000), {
        page_type: 'landing',
        visibility_change: true,
        page_visible: isVisible,
        time_on_page: timeOnPage
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [trackTimeOnPage]);

  // Track user interactions
  useEffect(() => {
    const handleUserInteraction = (eventType: string) => {
      const timeOnPage = Date.now() - startTime.current;
      
      trackTimeOnPage(Math.floor(timeOnPage / 1000), {
        page_type: 'landing',
        user_interaction: eventType,
        time_on_page: timeOnPage,
        active_user: true
      });
    };

    const handleClick = () => handleUserInteraction('click');
    const handleKeyPress = () => handleUserInteraction('keypress');
    const handleMouseMove = () => handleUserInteraction('mousemove');

    // Throttle mouse move events
    let mouseMoveTimeout: NodeJS.Timeout;
    const throttledMouseMove = () => {
      clearTimeout(mouseMoveTimeout);
      mouseMoveTimeout = setTimeout(handleMouseMove, 5000);
    };

    document.addEventListener('click', handleClick);
    document.addEventListener('keypress', handleKeyPress);
    document.addEventListener('mousemove', throttledMouseMove);

    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keypress', handleKeyPress);
      document.removeEventListener('mousemove', throttledMouseMove);
      clearTimeout(mouseMoveTimeout);
    };
  }, [trackTimeOnPage]);

  return null; // This component doesn't render anything
} 